import React, { Suspense, lazy } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import ErrorBoundary from './components/layout/ErrorBoundary';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PersonProvider, usePerson } from './context/PersonContext';
import ProtectedRoute from './components/layout/ProtectedRoute';
import FriendRequestNotification from './components/layout/FriendRequestNotification';

const LoginPage      = lazy(() => import('./pages/LoginPage'));
const RegisterPage   = lazy(() => import('./pages/RegisterPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const LifeGridPage   = lazy(() => import('./pages/LifeGridPage'));
const LandingPage    = lazy(() => import('./pages/LandingPage'));
const CommunityPage  = lazy(() => import('./pages/CommunityPage'));
const ProfilePage    = lazy(() => import('./pages/ProfilePage'));
const FriendGridPage = lazy(() => import('./pages/FriendGridPage'));

const PageLoader = () => <div className="min-h-screen bg-[#0d0e14]" />;

const RootRedirect = () => {
  const { user, loading } = useAuth();
  const { persons, loading: personLoading, fetchError } = usePerson();

  if (loading || personLoading) return null;
  if (!user) return <Navigate to="/landing" replace />;
  if (persons.length === 0 && !fetchError) return <Navigate to="/onboarding" replace />;
  return <Navigate to="/grid" replace />;
};

const AppRoutes = () => (
  <>
  <FriendRequestNotification />
  <Routes>
    <Route path="/landing" element={<LandingPage />} />
    <Route path="/login" element={<LoginPage />} />
    <Route path="/register" element={<RegisterPage />} />
    <Route
      path="/onboarding"
      element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/grid"
      element={
        <ProtectedRoute>
          <LifeGridPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/community"
      element={
        <ProtectedRoute>
          <CommunityPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/profile"
      element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/grid/friend/:userId"
      element={
        <ProtectedRoute>
          <FriendGridPage />
        </ProtectedRoute>
      }
    />
    <Route path="/" element={<RootRedirect />} />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
  </>
);

function App() {
  return (
    <ThemeProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <PersonProvider>
              <Suspense fallback={<PageLoader />}>
                <AppRoutes />
              </Suspense>
            </PersonProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
